"use client";

import {
    AreaData,
    AreaSeries as AreaSeriesConst, // 导入系列构造器
    AreaSeriesPartialOptions,
    ISeriesApi,
    LineStyle,
    LineType,
    WhitespaceData,
} from "lightweight-charts";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { useChart } from "./context";
// 自动缩放策略：避免波动太小时图表看起来过于剧烈
export const autoscaleInfoProvider = ( original: () => any ) => {
    const res = original();
    if ( res?.priceRange ) {
        const { minValue, maxValue } = res.priceRange;
        const diff = maxValue - minValue;
        // 设定最小波动范围 (0.2%)，避免噪音被放大
        const minDiff = ( ( minValue + maxValue ) / 2 ) * 0.005;
        if ( diff < minDiff ) {
            const center = ( minValue + maxValue ) / 2;
            const half = minDiff / 2;
            return { priceRange: { minValue: center - half, maxValue: center + half } };
        }
    }
    return res;
};
export interface AreaSeriesProps {
    data: (AreaData | WhitespaceData)[];
    options?: AreaSeriesPartialOptions;
    appendData?: AreaData;
    lastPrice?: number;
}

const getRgbaColor = ( hexColor: string, alpha: number ) => {
    const normalizedColor = hexColor.replace( "#", "" );
    const fullHexColor = normalizedColor.length === 3
        ? normalizedColor.split( "" ).map( ( value ) => value + value ).join( "" )
        : normalizedColor;
    const colorValue = Number.parseInt( fullHexColor, 16 );
    const red = ( colorValue >> 16 ) & 255;
    const green = ( colorValue >> 8 ) & 255;
    const blue = colorValue & 255;

    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const getAreaGradientOptions = ( lineColor: string ) => ( {
    topColor: getRgbaColor( lineColor, 0.2 ),
    bottomColor: getRgbaColor( lineColor, 0 ),
} );

/**
 * 面积图系列组件
 */
export const AreaSeries = forwardRef<ISeriesApi<"Area"> | null, AreaSeriesProps>(
    ( { data, options, appendData, lastPrice }, ref ) => {
        const { chart } = useChart();
        const seriesRef = useRef<ISeriesApi<"Area"> | null>( null );
        const lastPriceLineRef = useRef<any>( null );

        useEffect( () => {
            if ( !chart ) return;
            const lineColor = options?.lineColor ?? "#265CFF";

            const series = chart.addSeries( AreaSeriesConst, {
                lineColor,
                ...getAreaGradientOptions( lineColor ),
                lastValueVisible: false,
                crosshairMarkerVisible: false,
                priceLineVisible: false,
                lineWidth: 2,
                lineType: LineType.Curved, // 设置为曲线类型，使折线更平滑
                autoscaleInfoProvider,
                ...options,
            } );

            series.setData( data );
            seriesRef.current = series;

            return () => {
                if ( chart ) {
                    try {
                        chart.removeSeries( series );
                    } catch {
                        // Series may already be removed during rapid chart remounts.
                    }
                }
            };
        }, [ chart ] );

        useEffect( () => {
            if ( seriesRef.current && options ) {
                const lineColor = options.lineColor;
                seriesRef.current.applyOptions( {
                    ...( lineColor ? getAreaGradientOptions( lineColor ) : {} ),
                    ...options,
                } );
            }
        }, [ options ] );

        useEffect( () => {
            if ( seriesRef.current ) {
                seriesRef.current.setData( data );
            }
        }, [ data ] );

        useEffect( () => {
            if ( seriesRef.current && appendData ) {
                seriesRef.current.update( appendData );
            }
        }, [ appendData ] );

        // 处理最后价格线 - 显示当前最新价格
        useEffect( () => {
            if ( seriesRef.current && lastPrice !== undefined ) {
                // 删除旧的价格线
                if ( lastPriceLineRef.current ) {
                    try {
                        seriesRef.current.removePriceLine( lastPriceLineRef.current );
                    } catch {
                        // Price line may already be detached during rapid updates.
                    }
                }
                // 创建新的价格线
                lastPriceLineRef.current = seriesRef.current.createPriceLine( {
                    price: lastPrice,
                    color: "#FF3D4C",
                    lineWidth: 1 as 1 | 2 | 3 | 4,
                    lineStyle: LineStyle.Dashed,
                    axisLabelVisible: false,
                } );
            }
        }, [ lastPrice ] );

        // 暴露系列实例给外部
        useImperativeHandle( ref, () => seriesRef.current!, [ seriesRef.current ] );

        return null;
    },
);

AreaSeries.displayName = "AreaSeries";
